import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, BarChart3, Search, FileDown, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

const fmtCLP = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("es-CL");
const PIE_COLORS = ["#0FB4B7", "#384091", "#D97706", "#059669", "#DC2626", "#534AB7", "#9CA3AF"];

function Kpi({ label, value, color }) {
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4 text-center">
        <p className="text-2xl font-bold" style={{ color }}>{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

function BarRow({ nombre, count, sub, max, color }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-44 truncate text-gray-700" title={nombre}>
        {nombre}{sub ? <span className="text-gray-400 text-xs ml-1">{sub}</span> : null}
      </span>
      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
        <div className="h-3 rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-8 text-right font-medium text-gray-600">{count}</span>
    </div>
  );
}

export default function DashboardReportes() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tecnicos, setTecnicos] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({
    tipo: "todos", tecnico: "todos", cliente: "", fecha_desde: "", fecha_hasta: "",
  });

  const buildParams = useCallback((f) => {
    const p = new URLSearchParams();
    if (f.tipo && f.tipo !== "todos") p.set("tipo", f.tipo);
    if (f.tecnico && f.tecnico !== "todos") p.set("tecnico", f.tecnico);
    if (f.cliente) p.set("cliente", f.cliente);
    if (f.fecha_desde) p.set("fecha_desde", f.fecha_desde);
    if (f.fecha_hasta) p.set("fecha_hasta", f.fecha_hasta);
    return p.toString();
  }, []);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const r = await api.get(`/api/dashboard?${buildParams(f)}`);
      setData(r.data);
    } catch { toast.error("No se pudo cargar el dashboard"); }
    finally { setLoading(false); }
  }, [api, buildParams]);

  useEffect(() => {
    api.get("/api/auth/technicians").then((r) => setTecnicos(r.data)).catch(() => {});
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uf = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const clearFilters = () => {
    const empty = { tipo: "todos", tecnico: "todos", cliente: "", fecha_desde: "", fecha_hasta: "" };
    setFilters(empty);
    load(empty);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/api/dashboard/pdf?${buildParams(filters)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "dashboard_odontofix.pdf"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Dashboard PDF descargado");
    } catch { toast.error("Error al descargar el PDF"); }
    finally { setDownloading(false); }
  };

  const rep = data?.reportes || {};
  const cot = data?.cotizaciones || {};
  const porMes = data?.por_mes || [];
  const porTecnico = data?.por_tecnico || [];
  const topClientes = data?.top_clientes || [];
  const tiposFalla = data?.tipos_falla || [];
  const repuestosTop = data?.repuestos_top || [];
  const demanda = data?.demanda_semana || [];
  const diaMas = data?.dia_mas_demanda;
  const diaMenos = data?.dia_menos_demanda;
  const fallaTop = data?.falla_top;

  const maxTec = Math.max(1, ...porTecnico.map((x) => x.count));
  const maxCli = Math.max(1, ...topClientes.map((x) => x.count));
  const maxRep = Math.max(1, ...repuestosTop.map((x) => x.cantidad));

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-[#0FB4B7]" />
            <span className="text-sm font-semibold text-[#384091]">Dashboard Ejecutivo</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#384091] hover:bg-[#2D3375] text-white" onClick={downloadPdf} disabled={downloading}>
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}
              Descargar PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* Filtros */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div><Label className="text-xs">Tipo</Label>
                <Select value={filters.tipo} onValueChange={(v) => uf("tipo", v)}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="preventiva">Preventivas</SelectItem>
                    <SelectItem value="correctiva">Correctivas</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Técnico</Label>
                <Select value={filters.tecnico} onValueChange={(v) => uf("tecnico", v)}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {tecnicos.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Cliente</Label>
                <Input value={filters.cliente} onChange={(e) => uf("cliente", e.target.value)} placeholder="Buscar..." className="h-9 text-xs" /></div>
              <div><Label className="text-xs">Fecha desde</Label>
                <Input type="date" value={filters.fecha_desde} onChange={(e) => uf("fecha_desde", e.target.value)} className="h-9 text-xs" /></div>
              <div><Label className="text-xs">Fecha hasta</Label>
                <Input type="date" value={filters.fecha_hasta} onChange={(e) => uf("fecha_hasta", e.target.value)} className="h-9 text-xs" /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={() => load(filters)}>
                <Search className="h-3.5 w-3.5 mr-1" /> Aplicar filtros
              </Button>
              <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar</Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <p className="text-center text-gray-400 py-12">Cargando indicadores...</p>
        ) : !data ? (
          <p className="text-center text-gray-400 py-12">No se pudieron cargar los indicadores.</p>
        ) : (
          <>
            {/* KPIs reportes */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Kpi label="Total servicios" value={rep.total || 0} color="#384091" />
              <Kpi label="Este mes" value={rep.mes_actual || 0} color="#0FB4B7" />
              <Kpi label="Preventivas" value={rep.mp || 0} color="#0FB4B7" />
              <Kpi label="Correctivas" value={rep.mc || 0} color="#534AB7" />
              <Kpi label="Completados" value={rep.completados || 0} color="#059669" />
              <Kpi label="Correos enviados" value={rep.correos_enviados || 0} color="#D97706" />
            </div>

            {/* Análisis de demanda semanal */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-[#384091] mb-3">Análisis de demanda semanal</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={demanda}>
                    <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Servicios" radius={[4, 4, 0, 0]}>
                      {demanda.map((d, i) => (
                        <Cell key={i} fill={diaMas && d.dia === diaMas.dia ? "#059669" : diaMenos && d.dia === diaMenos.dia ? "#D97706" : "#0FB4B7"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-4 mt-2 justify-center text-sm">
                  {diaMas && (
                    <span className="flex items-center gap-1 text-emerald-700">
                      <TrendingUp className="h-4 w-4" /> Mayor demanda: <b>{diaMas.dia}</b> ({diaMas.count})
                    </span>
                  )}
                  {diaMenos && (
                    <span className="flex items-center gap-1 text-amber-700">
                      <TrendingDown className="h-4 w-4" /> Menor demanda: <b>{diaMenos.dia}</b> ({diaMenos.count})
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Reportes por mes */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-[#384091] mb-3">Servicios por mes</h3>
                {porMes.length === 0 ? (
                  <p className="text-sm text-gray-400">Sin datos.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={porMes}>
                      <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="mp" name="Preventivas" stackId="a" fill="#0FB4B7" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="mc" name="Correctivas" stackId="a" fill="#384091" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Por técnico */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-[#384091] mb-3">Servicios por técnico</h3>
                  {porTecnico.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin datos.</p>
                  ) : (
                    <div className="space-y-2">
                      {porTecnico.map((t) => (
                        <BarRow key={t.nombre} nombre={t.nombre} sub={`MP ${t.mp || 0} / MC ${t.mc || 0}`} count={t.count} max={maxTec} color="#0FB4B7" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top clientes */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-[#384091] mb-3">Top clientes</h3>
                  {topClientes.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin datos.</p>
                  ) : (
                    <div className="space-y-2">
                      {topClientes.map((c) => (
                        <BarRow key={c.nombre} nombre={c.nombre} count={c.count} max={maxCli} color="#384091" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Tipos de falla */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[#384091]">Tipos de falla (correctivas)</h3>
                    {fallaTop && <span className="text-xs text-gray-500">Más frecuente: <b className="text-[#384091]">{fallaTop.tipo}</b></span>}
                  </div>
                  {tiposFalla.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin correctivas registradas.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={230}>
                      <PieChart>
                        <Pie data={tiposFalla} dataKey="count" nameKey="tipo" cx="50%" cy="50%"
                          outerRadius={80} label={(e) => `${e.tipo} (${e.count})`} labelLine={false}>
                          {tiposFalla.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Repuestos más usados */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-[#384091] mb-3">Repuestos más utilizados</h3>
                  {repuestosTop.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin repuestos registrados.</p>
                  ) : (
                    <div className="space-y-2">
                      {repuestosTop.map((r) => (
                        <BarRow key={r.nombre} nombre={r.nombre} sub={`${r.veces}x`} count={r.cantidad} max={maxRep} color="#059669" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* KPIs cotizaciones */}
            <div>
              <h2 className="text-sm font-semibold text-[#384091] mb-2">Cotizaciones</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="Total" value={cot.total || 0} color="#384091" />
                <Kpi label="Enviadas" value={cot.enviadas || 0} color="#059669" />
                <Kpi label="Monto cotizado" value={fmtCLP(cot.monto_total)} color="#0FB4B7" />
                <Kpi label="Monto enviado" value={fmtCLP(cot.monto_enviadas)} color="#534AB7" />
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
